import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Box, IconButton, Typography, Paper, useTheme, useMediaQuery, Fade, Slide, TextField, Switch, FormControlLabel, Tooltip } from '@mui/material';
import { 
  KeyboardArrowLeft, 
  KeyboardArrowRight, 
  Notes, 
  Settings,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  FastRewind as FastRewindIcon,
  FastForward as FastForwardIcon,
  CropFree as CropFreeIcon,
  GridOn as GridOnIcon,
  Movie as MovieIcon,
  AccessTime as AccessTimeIcon,
  CameraAlt as CameraAltIcon,
  BurstMode as BurstModeIcon,
  Share as ShareIcon,
  Fullscreen as FullscreenIcon,
  PhotoLibrary as PhotoLibraryIcon,
  Subtitles as SubtitlesIcon,
  VideoLibrary as VideoLibraryIcon,
  School as SchoolIcon,
  CastConnected as CastConnectedIcon
} from '@mui/icons-material';
import NumberKeyPanel from './NumberKeyPanel';
import { NumberKeyState, MButtonState } from '../types';

interface SwipeableControlPanelProps {
  // 控制面板相關 props
  numberKeys: { [key: string]: NumberKeyState };
  isDeleteMode: boolean;
  mButtonState: MButtonState;
  vbids: any[];
  handlers: any;
  config: any;
  getSButtonStyle: any;
  setVbids: any;
  exportVBIDsToFile: any;
  exportFirestoreData: any;
  videoUrl?: string;
  user?: any;
  
  // 播放控制相關 props
  isPlaying?: boolean;
  onPlayPause?: () => void;
  onRewind?: () => void;
  onStepBackward?: () => void;
  onStepForward?: () => void;
  onFastForward?: () => void;
  fastForwardSeconds?: number;
  stepUnit?: string;
  
  // 工具列相關 props
  showSafetyFrame?: boolean;
  onSafetyFrameClick?: () => void;
  gridMode?: string;
  onGridLinesClick?: () => void;
  showMovieMask?: boolean;
  onMovieMaskToggle?: () => void;
  showTimecode?: boolean;
  onTimecodeClick?: () => void;
  onScreenshotClick?: () => void;
  onShareClick?: () => void;
  onFullScreenClick?: () => void;
  
  // 同步播放相關 props
  isOnAir?: boolean;
  syncManagerOpen?: boolean;
  onSyncToggle?: () => void;
  
  // Note 記錄相關 props
  onOpenNoteRecords?: () => void;
  noteRecordsDialogOpen?: boolean;
  setNoteRecordsDialogOpen?: (open: boolean) => void;
  
  // 文字內容相關 props - 與橫式佈局保持一致
  subtitleContent?: string;
  setSubtitleContent?: (content: string) => void;
  setShowAuthDialog?: (show: boolean) => void;
  currentSubtitleKey?: string;
  tabNumberKeys?: { [tabValue: number]: { [key: string]: NumberKeyState } };
  currentDocumentId?: string;
  currentFirestoreId?: string;
  currentViewpointName?: string;
  
  // 其他 props
  isMobile?: boolean;
  tabValue?: number;
  
  // 導航相關
  onNavigateToManagePages?: () => void;
  
  // 知識管理相關
  onOpenKnowledgeManagement?: () => void;
  
  // 初始狀態控制
  initialActivePanel?: number; // 0: 功能控制, 1: Note 記錄, 2: 知識管理, 3: 影片管理
  onActivePanelChange?: (panelIndex: number) => void; // 面板切換回調
}

const SwipeableControlPanel: React.FC<SwipeableControlPanelProps> = ({
  numberKeys,
  isDeleteMode,
  mButtonState,
  vbids,
  handlers,
  config,
  getSButtonStyle,
  setVbids,
  exportVBIDsToFile,
  exportFirestoreData,
  videoUrl,
  user,
  // 播放控制相關
  isPlaying = false,
  onPlayPause,
  onRewind,
  onStepBackward,
  onStepForward,
  onFastForward,
  fastForwardSeconds = 5,
  stepUnit = 'second',
  // 工具列相關
  showSafetyFrame = false,
  onSafetyFrameClick,
  gridMode = 'none',
  onGridLinesClick,
  showMovieMask = false,
  onMovieMaskToggle,
  showTimecode = true,
  onTimecodeClick,
  onScreenshotClick,
  onShareClick,
  onFullScreenClick,
  // 同步播放相關
  isOnAir = false,
  syncManagerOpen = false,
  onSyncToggle,
  // Note 記錄相關
  onOpenNoteRecords,
  noteRecordsDialogOpen,
  setNoteRecordsDialogOpen,
  // 文字內容相關 - 與橫式佈局保持一致
  subtitleContent = '',
  setSubtitleContent,
  setShowAuthDialog,
  currentSubtitleKey,
  tabNumberKeys,
  currentDocumentId = '',
  currentFirestoreId = '',
  currentViewpointName = '',
  // 其他
  isMobile = false,
  tabValue = 3,
  // 導航相關
  onNavigateToManagePages,
  // 知識管理相關
  onOpenKnowledgeManagement,
  // 初始狀態控制
  initialActivePanel = 0,
  onActivePanelChange
}) => {
  const componentIdRef = useRef(`SwipeableControlPanel-${Date.now()}`);
  
  
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down('md')) || isMobile;
  const [activePanel, setActivePanel] = useState(initialActivePanel);
  const [showNoteInput, setShowNoteInput] = useState(initialActivePanel === 1);
  const [showFourthRow, setShowFourthRow] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // 面板標題
  const panelTitles = ['功能控制', 'Note 記錄', '知識管理', '影片管理'];
  const panelIcons = [<Settings />, <Notes />, <SchoolIcon />, <VideoLibraryIcon />];

  // 初始化效果 - 只在第一次渲染時執行
  useEffect(() => {
    if (!isInitialized) {
      // 使用與MainPage一致的isPortrait判斷邏輯
      const aspectRatio = window.innerWidth / window.innerHeight;
      const isPortrait = window.innerWidth < 800 || (aspectRatio > 1.8 && window.innerHeight > window.innerWidth);
      
      if (isPortrait && initialActivePanel === 0) {
        // 如果是直式佈局且初始是功能控制，則滾動到 138px
        setTimeout(() => {
          window.scrollTo({ top: 138, behavior: 'smooth' });
        }, 100);
      } else if (isPortrait && initialActivePanel === 1) {
        // 如果是直式佈局且初始是 Note 記錄，則滾動到 138px（與功能控制相同）
        setTimeout(() => {
          window.scrollTo({ top: 138, behavior: 'smooth' });
        }, 100);
      }
      
      setIsInitialized(true);
    }
  }, [isInitialized]); // 移除 initialActivePanel 依賴，避免重複觸發

  // 響應 initialActivePanel 的變化
  useEffect(() => {
    if (initialActivePanel !== activePanel) {
      // initialActivePanel 變化
      setActivePanel(initialActivePanel);
      setShowNoteInput(initialActivePanel === 1);
      // 🔧 修復：保持第4列工具列始終顯示
      // setShowFourthRow(initialActivePanel === 0);
      
      // 觸發滾動邏輯（與手動點擊按鈕相同的行為）
      if (initialActivePanel === 1) {
        // 切換到 Note 記錄：執行滾動
        // 自動切換到Note記錄面板 - 執行滾動
        
        // 檢查是否為直式佈局 - 使用與MainPage一致的判斷邏輯
        const aspectRatio = window.innerWidth / window.innerHeight;
        const isPortrait = window.innerWidth < 800 || (aspectRatio > 1.8 && window.innerHeight > window.innerWidth);
        
        if (isPortrait) {
          const currentPosition = window.pageYOffset;
          const targetPosition = 280; // Note記錄滾動到280px位置

          // Note記錄自動滾動
          
          // 添加延遲確保DOM已更新
          setTimeout(() => {
            window.scrollTo({ top: targetPosition, behavior: 'smooth' });
            // 滾動執行完成
          }, 100);
        } else {
          // Note記錄 - 非直式佈局，無需滾動
        }
      } else if (initialActivePanel === 0) {
        // 切換到功能控制：執行滾動
        // 自動切換到功能控制面板
        
        // 檢查是否為直式佈局 - 使用與MainPage一致的判斷邏輯
        const aspectRatio = window.innerWidth / window.innerHeight;
        const isPortrait = window.innerWidth < 800 || (aspectRatio > 1.8 && window.innerHeight > window.innerWidth);
        
        if (isPortrait) {
          const currentPosition = window.pageYOffset;
          const targetPosition = 188; // 功能控制滾動到188px位置

          // 功能控制自動滾動
          window.scrollTo({ top: targetPosition, behavior: 'smooth' });
        } else {
          // 功能控制 - 非直式佈局，無需滾動
        }
      }
    }
  }, [initialActivePanel]);

  // 處理面板切換
  const handlePanelSwitch = (index: number) => {
    // 面板切換請求
    if (index === activePanel) {
      // 已是當前面板，取消切換
      return;
    }
    
    // 檢查是否為直式佈局 - 使用與MainPage一致的判斷邏輯
    const aspectRatio = window.innerWidth / window.innerHeight;
    const isPortrait = window.innerWidth < 800 || (aspectRatio > 1.8 && window.innerHeight > window.innerWidth);
    // 佈局檢查
    
    // 如果是導航到其他頁面的選項，不要更新 activePanel
    if (index === 2 || index === 3) {
      // 直接處理導航或其他功能，不更新 activePanel
    } else {
      setActivePanel(index);
      // 通知父組件面板已切換
      if (onActivePanelChange) {
        onActivePanelChange(index);
      }
    }
    
    if (index === 0) {
      // 切換到功能控制：還原第4列工具列並執行與Play鍵相同的滾動
      // 切換到功能控制面板
      setShowNoteInput(false);
      setShowFourthRow(true);
      
      if (isPortrait) {
        const currentPosition = window.pageYOffset;
        const targetPosition = 188; // 修改：功能控制滾動到188px位置

        // 功能控制滾動
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      } else {
        console.log('🎛️ 功能控制 - 非直式佈局，無需滾動');
      }
    } else if (index === 1) {
      // 切換到 Note 記錄：保持第4列工具列顯示，顯示輸入框並滾動
      // 切換到Note記錄面板
      setShowNoteInput(true);
      // 🔧 修復：保持第4列工具列始終顯示，避免時間碼按鈕消失
      // setShowFourthRow(false);
      
      if (isPortrait) {
        const currentPosition = window.pageYOffset;
        const targetPosition = 280; // 修改：Note記錄滾動到280px位置

        // Note記錄滾動
        window.scrollTo({ top: targetPosition, behavior: 'smooth' });
      } else {
        // Note記錄 - 非直式佈局，無需滾動
      }
    } else if (index === 2) {
      // 切換到知識管理：打開知識管理 Dialog
      // 點擊知識管理按鈕
      if (onOpenKnowledgeManagement) {
        // 調用知識管理函數
        onOpenKnowledgeManagement();
      } else {
        console.log('❌ 知識管理函數未定義');
      }
    } else if (index === 3) {
      // 切換到影片管理：導航到 ManagePages
      // 點擊影片管理按鈕
      if (onNavigateToManagePages) {
        // 調用導航函數
        // 延遲執行導航，避免事件衝突
        setTimeout(() => {
          onNavigateToManagePages();
        }, 100);
      } else {
        console.log('❌ 導航函數未定義');
      }
    }
  };

  // 渲染控制面板
  const renderControlPanel = () => (
    <Box sx={{ 
      width: '100%', 
      height: 'auto', // 改為自動高度
      display: 'flex',
      flexDirection: 'column',
      p: 0.5, // 減少內邊距
      minHeight: 'auto', // 改為自動最小高度，移除固定高度
      overflow: 'visible' // 改為 visible 以確保內容不被隱藏
    }}>
      {/* 第一行：播放控制按鈕 */}
      <Box sx={{
        width: '120%', // 增加寬度到 120%
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mb: 0.25, // 進一步減少底部邊距
        py: 0.25, // 進一步減少內邊距
        borderRadius: 1,
        // 直式構圖時整體縮小 80%
        transform: 'scale(0.8)',
        transformOrigin: 'center center',
        marginLeft: '-10%', // 向左偏移以保持居中
        marginRight: '-10%' // 向右偏移以保持居中
      }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center',
          flexWrap: 'nowrap', // 改為不換行
          justifyContent: 'space-between', // 改為兩端對齊，中間平均分配
          width: '100%',
          maxWidth: '500px', // 限制最大寬度為 500px
          px: 1 // 左右內邊距，讓按鈕貼齊邊框
        }}>
          {/* 快退 5 秒 - 與橫式構圖樣式一致 */}
          <IconButton 
            onClick={onRewind}
            disabled={tabValue === 4 || tabValue === 5}
            size="small"
            className="playback-control-btn"
            sx={{ 
              transform: 'scale(1.2)',
              border: '2px solid #e0e0e0',
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px', // 縮小內邊距
              height: '36px', // 與 check 標記鍵相同高度
              minHeight: '36px', // 設定最小高度
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              }
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              -{fastForwardSeconds}s
            </Typography>
            <FastRewindIcon />
          </IconButton>

          {/* 快退 1 秒 - 與橫式構圖樣式一致 */}
          <IconButton 
            onClick={onStepBackward}
            disabled={tabValue === 4 || tabValue === 5}
            size="small"
            className="playback-control-btn"
            sx={{ 
              transform: 'scale(1.2)',
              border: '2px solid #e0e0e0',
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px', // 縮小內邊距
              height: '36px', // 與 check 標記鍵相同高度
              minHeight: '36px', // 設定最小高度
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              }
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              {stepUnit === 'second' ? '-1s' : '-1f'}
            </Typography>
            <FastRewindIcon fontSize="small" />
          </IconButton>

          {/* 播放/暫停按鈕 - 與橫式構圖樣式一致 */}
          <IconButton 
            onClick={() => {
              // 執行原有的播放/暫停功能
              onPlayPause();
            }}
            disabled={tabValue === 4 || tabValue === 5}
            className="playback-control-btn"
            sx={{ 
              transform: 'scale(1.2)', // 與其他按鈕相同的縮放比例
              backgroundColor: '#d32f2f',
              borderRadius: '8px',
              color: 'white',
              height: '36px', // 與 check 標記鍵相同高度
              minHeight: '36px', // 設定最小高度
              minWidth: '60px', // 從 40px 增加到 60px
              padding: '4px 8px', // 縮小內邊距
              '&:hover': {
                backgroundColor: '#b71c1c'
              }
            }}
          >
            {isPlaying ? <PauseIcon /> : <PlayArrowIcon />}
          </IconButton>

          {/* 快進 1 秒 - 與橫式構圖樣式一致 */}
          <IconButton 
            onClick={onStepForward}
            disabled={tabValue === 4 || tabValue === 5}
            size="small"
            className="playback-control-btn"
            sx={{ 
              transform: 'scale(1.2)',
              border: '2px solid #e0e0e0',
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px', // 縮小內邊距
              height: '36px', // 與 check 標記鍵相同高度
              minHeight: '36px', // 設定最小高度
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              }
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              {stepUnit === 'second' ? '+1s' : '+1f'}
            </Typography>
            <FastForwardIcon fontSize="inherit" />
          </IconButton>

          {/* 快進 5 秒 - 與橫式構圖樣式一致 */}
          <IconButton 
            onClick={onFastForward}
            disabled={tabValue === 4 || tabValue === 5}
            size="small"
            className="playback-control-btn"
            sx={{ 
              transform: 'scale(1.2)',
              border: '2px solid #e0e0e0',
              borderRadius: '16px',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              padding: '4px 8px', // 縮小內邊距
              height: '36px', // 與 check 標記鍵相同高度
              minHeight: '36px', // 設定最小高度
              '&:hover': {
                borderColor: '#d32f2f',
                backgroundColor: 'rgba(211, 47, 47, 0.1)',
              }
            }}
          >
            <Typography variant="caption" sx={{ mr: 0.5 }}>
              +{fastForwardSeconds}s
            </Typography>
            <FastForwardIcon />
          </IconButton>
        </Box>
      </Box>
      
      {/* 第二行：NumberKeyPanel (包含功能模式列和數字鍵面板) */}
      <Box sx={{ flex: '0 0 auto', minHeight: 'auto' }}>
        <NumberKeyPanel
          numberKeys={numberKeys}
          isDeleteMode={isDeleteMode}
          mButtonState={mButtonState}
          vbids={vbids}
          handlers={handlers}
          config={config}
          getSButtonStyle={getSButtonStyle}
          setVbids={setVbids}
          exportVBIDsToFile={exportVBIDsToFile}
          exportFirestoreData={exportFirestoreData}
          videoUrl={videoUrl}
          user={user}
          currentTime={config.currentTime}  // 🔧 修復：傳遞 currentTime 以啟用放大動畫
          isPlaying={config.isPlaying}  // 🔧 新增：傳遞播放狀態
          autoShowNoteOnPass={false}  // 🔧 關閉自動切換 Note，避免干擾快進/快退
        />
      </Box>
      
      {/* 第四行：工具與狀態列 */}
      {showFourthRow && (
        <Box 
          sx={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '4px',
            mt: 0.5,
            pt: 0.5,
            pb: 0.5
          }}>
            {/* 安全框 */}
            <IconButton 
              onClick={onSafetyFrameClick}
              color={showSafetyFrame ? "primary" : "default"}
              size="small"
              sx={{ 
                opacity: showSafetyFrame ? 1 : 0.5,
                '& .MuiSvgIcon-root': { color: showSafetyFrame ? '#1976d2' : '#666666' },
                transform: 'scale(1.0)',
                flexShrink: 0
              }}
            >
              <CropFreeIcon fontSize="small" />
            </IconButton>

            {/* 格線 */}
            <IconButton 
              onClick={onGridLinesClick}
              color={gridMode !== 'none' ? "primary" : "default"}
              size="small"
              sx={{ 
                opacity: gridMode !== 'none' ? 1 : 0.5,
                '& .MuiSvgIcon-root': { color: gridMode !== 'none' ? '#1976d2' : '#666666' },
                transform: 'scale(1.0)',
                flexShrink: 0
              }}
            >
              <GridOnIcon fontSize="small" />
            </IconButton>
            
            {/* 上下遮黑 */}
            <IconButton 
              onClick={onMovieMaskToggle}
              color={showMovieMask ? "primary" : "default"}
              disabled={tabValue === 4 || tabValue === 5}
              size="small"
              sx={{
                transform: 'scale(1.0)',
                flexShrink: 0
              }}
            >
              <MovieIcon fontSize="small" />
            </IconButton>

            {/* 时间码 */}
            <IconButton 
              onClick={onTimecodeClick}
              disabled={tabValue > 3}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0,
                opacity: showTimecode ? 1 : 0.5,
                '& .MuiTypography-root': { color: '#000000' }
              }}
            >
              <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
                00:00:00
              </Typography>
            </IconButton>

            {/* 分享 */}
            <IconButton 
              onClick={onShareClick}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0,
                '& .MuiSvgIcon-root': { 
                  color: videoUrl ? '#1976d2' : '#666666' 
                }
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>

            {/* 截圖 */}
            <IconButton 
              onClick={onScreenshotClick}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0
              }}
            >
              <CameraAltIcon fontSize="small" />
            </IconButton>

            {/* 重點截圖 */}
            <IconButton 
              onClick={() => {}}
              disabled={tabValue === 3 || tabValue === 4 || tabValue === 5}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0,
                color: (tabValue === 3 || tabValue === 4 || tabValue === 5) ? '#999999' : 'inherit'
              }}
            >
              <BurstModeIcon fontSize="small" />
            </IconButton>

            {/* 圖片管理 */}
            <IconButton 
              onClick={() => {}}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0
              }}
            >
              <PhotoLibraryIcon fontSize="small" />
            </IconButton>

            {/* 同步連線播放 */}
            <IconButton 
              onClick={onSyncToggle}
              size="small"
              sx={{ 
                transform: 'scale(1.0)',
                flexShrink: 0,
                '& .MuiSvgIcon-root': { 
                  color: (isOnAir || syncManagerOpen) ? '#1976d2' : '#666666' 
                }
              }}
            >
              <CastConnectedIcon fontSize="small" />
            </IconButton>
        </Box>
      )}
    </Box>
  );


  // 渲染面板指示器
  const renderIndicators = () => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      gap: 0,
      py: 1,
      borderTop: '1px solid',
      borderColor: 'divider',
      backgroundColor: 'background.paper'
    }}>
      {panelTitles.map((title, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1,
            py: 0.25,
            borderRadius: 2,
            cursor: 'pointer',
            backgroundColor: activePanel === index ? 'primary.main' : 'transparent',
            color: activePanel === index ? 'white' : 'text.secondary',
            transition: 'all 0.2s ease',
            '&:hover': {
              backgroundColor: activePanel === index ? 'primary.dark' : 'action.hover'
            }
          }}
          onClick={() => handlePanelSwitch(index)}
        >
          {panelIcons[index]}
          <Typography 
            variant="caption" 
            fontWeight={activePanel === index ? 'bold' : 'normal'}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {title}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  // 渲染 Note 輸入框 - 直接使用橫式佈局的圓角文字框
  const renderNoteInput = () => {
    return (
      <Box sx={{ 
        width: '100%', 
        mt: 0.5, // 縮減間距：從 mt: 2 (16px) 改為 mt: 0.5 (4px)
        p: 1,
        backgroundColor: 'background.paper',
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider'
      }}>
        {/* 頂部控制區域 - 直式構圖時集中在畫面中央 */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center', // 改為置中對齊
          mb: 1,
          pb: 1,
          borderBottom: '1px solid #e0e0e0',
          gap: 2 // 添加間距讓三個物件分開
        }}>
          <Typography 
            variant="h6" 
            sx={{ 
              color: '#1976d2', // 保持藍色文字
              fontSize: '1.2rem', // 與時間碼相同：放大字體
              fontWeight: 700, // 與時間碼相同：粗體
              textAlign: 'center'
            }}
          >
            NO {currentSubtitleKey || '--'}
          </Typography>
          
          {/* 🔧 修改：字幕按鈕 - 自動填充 Note */}
          {config?.videoUrl && handlers?.onAutoFillNoteFromSRT ? (
            <Tooltip
              title={config.showSubtitleFillTooltip ? "字幕已下載完成，點擊填充字幕！" : "自動填充字幕到 Note（點擊後請稍等片刻）"}
              open={config.showSubtitleFillTooltip || undefined}
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={() => handlers.onAutoFillNoteFromSRT()}
                  disabled={config.isDownloadingSubtitles || !config.srtContent}
                  sx={{
                    color: '#9c27b0',
                    backgroundColor: 'rgba(156, 39, 176, 0.1)',
                    border: '2px solid #9c27b0',
                    opacity: (config.isDownloadingSubtitles || !config.srtContent) ? 0.5 : 1,
                    '&:hover': {
                      backgroundColor: 'rgba(156, 39, 176, 0.2)'
                    }
                  }}
                >
                  <SubtitlesIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            <Typography 
              variant="caption" 
              sx={{ 
                color: '#f44336',
                fontSize: '0.75rem',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                padding: '2px 4px',
                borderRadius: '4px'
              }}
            >
              無字幕按鈕
            </Typography>
          )}
          
          {/* 時間碼顯示區域 - 使用橫式佈局的邏輯 */}
          {(() => {
            // 🔧 修復：優先使用 numberKeys（YouTube Tab 的主要數據源），然後才使用 tabNumberKeys
            let finalKeyState = null;
            
            // 首先嘗試從 numberKeys 獲取（適用於 YouTube Tab）
            if (numberKeys && numberKeys[String(currentSubtitleKey)]) {
              finalKeyState = numberKeys[String(currentSubtitleKey)];
            }
            
            // 如果 numberKeys 中沒有，再嘗試從 tabNumberKeys 獲取
            if (!finalKeyState) {
              const currentTabNumberKeys = (tabNumberKeys?.[tabValue] || tabNumberKeys?.[3] || {});
              finalKeyState = currentTabNumberKeys[String(currentSubtitleKey)];
            }
            
            // 檢查 finalKeyState 是否存在
            if (!finalKeyState) {
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000', // 改為黑色
                    fontSize: '1.2rem', // 放大字體
                    fontWeight: 700, // 改為粗體
                    textAlign: 'center'
                    // 移除灰底外框
                  }}
                >
                  --:--:--
                </Typography>
              );
            }
            
            if (finalKeyState.timestamp !== null && finalKeyState.timestamp !== undefined && finalKeyState.timestamp >= 0 && finalKeyState.timestamp <= 1000000) {
              // 使用與 NoteRecordsDialog 相同的格式化邏輯
              const seconds = finalKeyState.timestamp;
              const totalSeconds = Math.floor(seconds);
              const hours = Math.floor(totalSeconds / 3600);
              const minutes = Math.floor((totalSeconds % 3600) / 60);
              const secs = totalSeconds % 60;
              const frames = Math.floor((seconds % 1) * 30);
              
              const pad = (num: number) => num.toString().padStart(2, '0');
              const formattedTimecode = `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frames)}`;
              const timeOnly = `${pad(hours)}:${pad(minutes)}:${pad(secs)}`; // 只取時間部分，不要幀數
              
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000', // 改為黑色
                    fontSize: '1.2rem', // 放大字體
                    fontWeight: 700, // 改為粗體
                    textAlign: 'center'
                    // 移除灰底外框
                  }}
                >
                  {timeOnly}
                </Typography>
              );
            } else if (finalKeyState.timecode && finalKeyState.timecode.trim() !== '' && finalKeyState.timecode !== '00:00:00 00' && finalKeyState.timecode !== '--:--:--') {
              // 只有在 timestamp 無效且 timecode 不是 00:00:00 00 時，才使用 timecode
              const timeParts = finalKeyState.timecode.split(' ');
              const timeOnly = timeParts[0]; // 取 "00:00:00" 部分
              
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000', // 改為黑色
                    fontSize: '1.2rem', // 放大字體
                    fontWeight: 700, // 改為粗體
                    textAlign: 'center'
                    // 移除灰底外框
                  }}
                >
                  {timeOnly}
                </Typography>
              );
            } else if (finalKeyState && finalKeyState.timestamp) {
              // 如果沒有 timecode 但有 timestamp，嘗試計算時間碼
              const formatTimecode = (seconds: number, frameOverride?: number | null): string => {
                // 🔧 修復：擴大時間範圍以支持長時間直播影片（最多支持約 278 小時）
                const MAX_VIDEO_DURATION = 1000000; // 支持長時間直播，約 278 小時
                if (typeof seconds !== 'number' || isNaN(seconds) || seconds < 0 || seconds > MAX_VIDEO_DURATION) {
                  console.warn(`⚠️ 異常時間戳: ${seconds} 秒，超出合理範圍 (0-${MAX_VIDEO_DURATION})，使用預設值`);
                  return '00:00:00 00';
                }
                
                const pad = (num: number): string => num.toString().padStart(2, '0');
                const hours = Math.floor(seconds / 3600);
                const minutes = Math.floor((seconds % 3600) / 60);
                const secs = Math.floor(seconds % 60);
                const frame = frameOverride !== undefined && frameOverride !== null ? frameOverride : Math.floor((seconds % 1) * 30);
                return `${pad(hours)}:${pad(minutes)}:${pad(secs)} ${pad(frame)}`;
              };
              
              const calculatedTimecode = formatTimecode(finalKeyState.timestamp, finalKeyState.frame || 0);
              const timeParts = calculatedTimecode.split(' ');
              const timeOnly = timeParts[0];
              
              
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000', // 改為黑色
                    fontSize: '1.2rem', // 放大字體
                    fontWeight: 700, // 改為粗體
                    textAlign: 'center'
                    // 移除灰底外框
                  }}
                >
                  {timeOnly}
                </Typography>
              );
            } else {
              // 如果時間碼無效，顯示預設值
              return (
                <Typography 
                  variant="body2" 
                  sx={{ 
                    color: '#000000', // 改為黑色
                    fontSize: '1.2rem', // 放大字體
                    fontWeight: 700, // 改為粗體
                    textAlign: 'center'
                    // 移除灰底外框
                  }}
                >
                  --:--:--
                </Typography>
              );
            }
          })()}

          {/* 右側控制開關 - 直式構圖時設為 disabled */}
          <FormControlLabel
            control={
              <Switch
                checked={false} // 直式構圖時設為關閉
                disabled={true} // 設為 disabled 狀態
                onChange={() => {}}
                color="primary"
                size="small"
              />
            }
            label="右側控制"
            labelPlacement="start"
            sx={{
              margin: 0,
              opacity: 0.5, // 降低透明度表示 disabled
              '& .MuiFormControlLabel-label': {
                fontSize: '0.7rem',
                color: '#999999', // 更淺的灰色表示 disabled
                fontWeight: 400
              }
            }}
          />
        </Box>
        
        {/* 文字輸入框區域 - 直接使用橫式佈局的圓角文字框 */}
        <Box sx={{ 
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'flex-start',
          pt: 0.5 // 縮減間距：從 pt: 1 (8px) 改為 pt: 0.5 (4px)
        }}>
          {/* 🔧 觀點標題 - 顯示 Firestore 中的實際觀點名稱 */}
          {currentViewpointName && (
            <Typography 
              sx={{ 
                width: '90%',
                color: '#666',
                fontSize: '0.875rem',
                fontWeight: 400,
                textAlign: 'left',
                m: 0,
                p: 0,
                mb: 0.5 // 與文字框保持一點點間距
              }}
            >
              {currentViewpointName}
            </Typography>
          )}
          
          {/* 直接使用橫式佈局的圓角文字框，不顯示字幕白板 */}
          <TextField
            multiline
            rows={6}
            value={subtitleContent}
            onChange={(e) => {
              // 與橫式佈局完全相同的邏輯：檢查用戶登入狀態
              if (!user?.email) {
                if (setShowAuthDialog) {
                  setShowAuthDialog(true);
                }
                return;
              }
              // 與橫式佈局完全相同：設置文字內容
              if (setSubtitleContent) {
                setSubtitleContent(e.target.value);
              }
            }}
            onClick={(e) => {
              // 阻止事件冒泡，防止觸發全局點擊事件
              e.stopPropagation();
            }}
            onMouseDown={(e) => {
              // 阻止事件冒泡，防止觸發全局點擊事件
              e.stopPropagation();
            }}
            onTouchStart={(e) => {
              // 阻止事件冒泡，防止觸發全局點擊事件
              e.stopPropagation();
            }}
            placeholder="在此輸入字幕內容..."
            variant="standard"
            size="small"
            sx={{
              width: '90%', // 與橫式佈局相同：寬度改為容器 90%
              flex: '1 1 auto', // 與橫式佈局相同：允許彈性增長和收縮
              alignSelf: 'stretch', // 與橫式佈局相同：底部拉伸貼齊底邊界
              margin: '0 auto', // 與橫式佈局相同：水平置中對齊
              minHeight: '80px', // 與橫式佈局相同：最小高度80px
              maxHeight: 'none', // 與橫式佈局相同：移除高度限制，讓內容自然增長
              mt: '3px', // 與橫式佈局相同：與大標區保持3px間距，讓佈局更緊湊
              '& .MuiInputBase-root': {
                width: '100% !important', // 與橫式佈局相同：強制內部元素寬度 100%
                fontSize: { xs: '1rem', sm: '1.1rem', md: '1.2rem' }, // 與橫式佈局相同：響應式字型大小
                color: '#333333', // 與橫式佈局相同：文字顏色
                backgroundColor: '#fff3e0', // 與橫式佈局相同：淺黃色背景
                borderRadius: '4px', // 與橫式佈局相同：圓角
                padding: '12px', // 與橫式佈局相同：增加內邊距，讓文字有更多空間
                height: 'auto', // 與橫式佈局相同：高度自動，隨內容增長
                minHeight: '56px', // 與橫式佈局相同：最小高度56px
                maxHeight: 'none', // 與橫式佈局相同：移除高度限制，讓淺黃色區自然增長
                border: 'none', // 與橫式佈局相同：移除邊框
                textAlign: 'left', // 與橫式佈局相同：左對齊，讓文字能充分利用空間
                marginBottom: '0px', // 與橫式佈局相同：保持無底部邊距
                display: 'flex', // 與橫式佈局相同：使用flexbox佈局
                alignItems: 'flex-start', // 與橫式佈局相同：文字從頂部開始對齊
                transition: 'height 0.2s ease', // 與橫式佈局相同：平滑的高度變化動畫
                overflow: 'visible', // 與橫式佈局相同：防止內容溢出
                '&:hover': {
                  backgroundColor: '#ffe0b2', // 與橫式佈局相同：hover 時稍微深一點的淺黃色
                },
                '&.Mui-focused': {
                  backgroundColor: '#ffffff', // 與橫式佈局相同：聚焦時白色背景
                  boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.2)', // 與橫式佈局相同：聚焦時藍色陰影
                },
                '&:before': { // 與橫式佈局相同：移除底部細黑線
                  borderBottom: 'none',
                },
                '&:after': { // 與橫式佈局相同：移除底部細黑線
                  borderBottom: 'none',
                },
              },
              '& .MuiInputBase-input': {
                width: '100% !important', // 與橫式佈局相同：強制輸入框寬度 100%
                textAlign: 'left !important', // 與橫式佈局相同：左對齊，讓文字能充分利用空間
                padding: '0', // 與橫式佈局相同：移除內部padding，讓文字能填滿整個區域
                height: 'auto', // 與橫式佈局相同：高度自動，隨內容增長
                lineHeight: '1.5', // 與橫式佈局相同：設定行高，讓文字更易讀
                resize: 'vertical', // 與橫式佈局相同：允許用戶手動調整高度
                '&::placeholder': {
                  color: '#999999', // 與橫式佈局相同：placeholder 顏色
                  opacity: 0.7, // 與橫式佈局相同：placeholder 透明度
                  textAlign: 'left !important', // 與橫式佈局相同：placeholder 左對齊
                },
              }
            }}
          />
        </Box>
      </Box>
    );
  };

  
  return (
    <Box 
      data-component="swipeable-control-panel" // 🔧 標記組件實例
      data-instance-id={componentIdRef.current} // 🔧 追蹤實例 ID
      sx={{ 
      width: '100%', 
      position: 'relative',
    }}>
      {/* 面板指示器 */}
      {renderIndicators()}
      
      {/* 控制面板內容 */}
      <Box sx={{ 
        width: '100%',
        minHeight: 'auto', // 改為自動高度，移除固定最小高度
        p: 0.5 // 縮減間距：從 p: 1 (8px) 改為 p: 0.5 (4px)
      }}>
        {renderControlPanel()}
      </Box>
      
      {/* Note 輸入框 - 在控制面板下方 */}
      {showNoteInput && renderNoteInput()}
    </Box>
  );
};

// 使用 React.memo 优化性能
export default React.memo(SwipeableControlPanel, (prevProps, nextProps) => {
  // 比较关键 props
  const criticalProps: Array<keyof SwipeableControlPanelProps> = [
    'numberKeys',
    'vbids',
    'isPlaying',
    'tabValue',
    'videoUrl',
    'showTimecode',
    'showSafetyFrame',
    'gridMode',
    'showMovieMask'
  ];
  
  for (const key of criticalProps) {
    if (prevProps[key] !== nextProps[key]) {
      return false; // 需要重新渲染
    }
  }
  
  return true; // 阻止重新渲染
});